import React, { useState, useRef, useEffect } from 'react';
import { 
  FileSearch, Download, Copy, Check, 
  AlertCircle, FileText, ArrowLeft, RefreshCw, 
  Upload, Sparkles, AlertTriangle, ListChecks, HelpCircle,
  Search, ShieldAlert, CheckCircle, Scale, Database, Globe, Printer
} from 'lucide-react';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import TenderPackageGeneratorView from './TenderPackageGeneratorView';

interface TenderAnalystProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const DEMO_PRESETS = [
  {
    titleUk: "💻 Закупівля комп'ютерної техніки та ПЗ (ДК 021:2015 30230000-0)",
    titleEn: "💻 IT Hardware & Software Procurement",
    id: "UA-2024-05-18-004512-a",
    text: `ПРЕДМЕТ ЗАКУПІВЛІ: Персональні комп'ютери, монітори та ліцензійне програмне забезпечення для відділу освіти.
Очікувана вартість: 1 450 000.00 грн з ПДВ.
Замовник: Управління освіти, молоді та спорту міської ради (код ЄДРПОУ: 02145896).
Кінцевий строк подання: 25.08.2026 18:00.

КВАЛІФІКАЦІЙНІ ВИМОГИ ТА УМОВИ ТД:
1. Наявність документально підтвердженого досвіду виконання аналогічного договору (не менше 3 договорів за 2024-2026 рр. на суму не менше 100% від очікуваної вартості).
2. Наявність сертифіката ISO 9001:2015, виданого органом сертифікації, акредитованим НААУ, виданого не раніше ніж за 30 днів до дати оголошення.
3. Довідка про наявність власного сервісного центру в радіусі 15 км від замовника з часом реагування не більше 2 годин.
4. Поставка товару протягом 3 календарних днів з моменту підписання договору.
5. Надання листа від виробника процесорів із підтвердженням авторизації учасника під конкретний номер процедури закупівлі.`
  },
  {
    titleUk: "☀️ Обслуговування дахових СЕС (ДК 021:2015 50530000-9)",
    titleEn: "☀️ Solar Power Station Maintenance",
    id: "UA-2024-06-12-007891-b",
    text: `ПРЕДМЕТ ЗАКУПІВЛІ: Послуги з технічного обслуговування, діагностики та очищення мережевих сонячних електростанцій потужністю 150 кВт.
Очікувана вартість: 480 000.00 грн з ПДВ.
Замовник: КП «Муніципальна енергетична компанія» (код ЄДРПОУ: 38451278).

ТЕХНІЧНІ ВИМОГИ:
- Проведення тепловізійного обстеження фотомодулів з наданням термограм у форматі FLIR.
- Вимірювання вольт-амперних характеристик (ВАХ) стрінгів.
- Наявність у персоналу допуску до робіт в електроустановках до та понад 1000 В (IV-V група з електробезпеки).
- Надання розрахунку собівартості робіт за нормативами РЕКН.`
  },
  {
    titleUk: "🛰️ Супутниковий моніторинг земель громади (ДК 021:2015 72310000-1)",
    titleEn: "🛰️ Satellite Land Monitoring Services",
    id: "UA-2024-07-01-001234-c",
    text: `ПРЕДМЕТ ЗАКУПІВЛІ: Послуги супутникового дистанційного зондування землі для інвентаризації та моніторингу сільськогосподарських угідь територіальної громади (площа 45 000 га).
Очікувана вартість: 620 000.00 грн з ПДВ.
Замовник: Виконавчий комітет міської ради (код ЄДРПОУ: 04052319).

ВИМОГИ ТД:
- Мультиспектральні знімки роздільної здатності не гірше 0.5 м/піксель.
- Побудова індексів NDVI, NDRE, NDWI.
- Виявлення самозахоплення земельних ділянок та нецільового використання.`
  }
];

const LOADING_STEPS_UK = [
  "Ініціалізація модуля Prozorro Deep Intelligence...",
  "Тендерний спеціаліст розбирає технічне завдання та кваліфікаційні вимоги за ст. 16...",
  "Комплаєнс-аналітик виявляє дискримінаційні норми за ст. 5, 22 та ПКМУ № 1178 для скарг в АМКУ...",
  "Тендерний керівник розраховує маржинальність та формує чек-лист подачі...",
  "Генерація ПОВНИХ ЗРАЗКІВ УСІХ ДОКУМЕНТІВ за стандартом ДСТУ 4163:2020..."
];

const LOADING_STEPS_EN = [
  "Initializing Prozorro Deep Intelligence AI module...",
  "Tender Specialist is parsing technical criteria and qualification metrics...",
  "Compliance Analyst is scanning for anti-competitive clauses and AMKU appeal grounds...",
  "Tender Lead is calculating margins and structuring the bidder checklist...",
  "Compiling FULL SAMPLES OF ALL BIDDER DOCUMENTS strictly per DSTU 4163:2020..."
];

export default function TenderAnalyst({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: TenderAnalystProps) {
  const isUk = language === 'uk';
  const [viewMode, setViewMode] = useState<'audit' | 'generator'>('audit');
  const [prozorroQuery, setProzorroQuery] = useState('');
  const [text, setText] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; size: number }[]>([]);
  
  const [isFetchingProzorro, setIsFetchingProzorro] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenderReport, setTenderReport] = useState<string | null>(null);
  const [fetchedTenderMeta, setFetchedTenderMeta] = useState<any | null>(null);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  // Fetch Live Data from Prozorro Open Data API
  const handleFetchProzorro = async () => {
    if (!prozorroQuery.trim()) {
      setError(isUk ? 'Введіть номер тендера Prozorro (наприклад, UA-2024-05-18-004512-a) або посилання.' : 'Enter Prozorro tender ID or URL.');
      return;
    }

    setIsFetchingProzorro(true);
    setError(null);

    try {
      const response = await fetch('/api/prozorro/fetch-tender', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenderQuery: prozorroQuery.trim() })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || (isUk ? 'Не вдалося завантажити дані з Prozorro.' : 'Failed to fetch Prozorro tender.'));
      }

      if (data.tender) {
        setFetchedTenderMeta(data.tender);
        setText((prev) => prev ? `${data.tender.formattedText}\n\n${prev}` : data.tender.formattedText);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Помилка отримання даних з Prozorro.' : 'Failed to fetch from Prozorro.'));
    } finally {
      setIsFetchingProzorro(false);
    }
  };

  // File Upload and Text Extraction
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setError(null);
    setIsExtracting(true);

    const newUploadedFiles = [...uploadedFiles];
    let concatenatedText = text;

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/extract-text', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || (isUk ? `Помилка вилучення тексту з файлу ${file.name}.` : `Failed to extract text from file ${file.name}.`));
        }

        const data = await response.json();
        newUploadedFiles.push({ name: file.name, size: file.size });
        concatenatedText = concatenatedText 
          ? `${concatenatedText}\n\n=== ВМІСТ ФАЙЛУ ${file.name.toUpperCase()} ===\n${data.text}`
          : `=== ВМІСТ ФАЙЛУ ${file.name.toUpperCase()} ===\n${data.text}`;
      }

      setUploadedFiles(newUploadedFiles);
      setText(concatenatedText);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Не вдалося прочитати файли.' : 'Failed to read files.'));
    } finally {
      setIsExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Run Tender Analysis
  const handleRunAudit = async () => {
    if (!text.trim()) {
      setError(isUk ? 'Введіть текст тендерної документації або завантажте файл.' : 'Please enter tender text or upload files.');
      return;
    }

    setIsAuditing(true);
    setError(null);
    setTenderReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) headers['x-gemini-api-key'] = customKey;

      const response = await fetch('/api/audit-tender', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text,
          useEnterpriseContext: !!useEnterpriseContext
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || (isUk ? 'Помилка аналізу тендеру аналітичною системою.' : 'Failed to audit tender.'));
      }

      const { jobId } = data;
      if (!jobId) {
        if (data.report) {
          setTenderReport(data.report);
          setIsAuditing(false);
          return;
        }
        throw new Error(isUk ? 'Сервер не надав ID завдання.' : 'Server did not provide job ID.');
      }

      // Poll job status
      const interval = setInterval(async () => {
        try {
          const statusRes = await fetch(`/api/job-status/${jobId}`);
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              clearInterval(interval);
              setIsAuditing(false);
              setTenderReport(statusData.report);
            } else if (statusData.status === 'failed') {
              clearInterval(interval);
              setIsAuditing(false);
              setError(statusData.error || (isUk ? 'Помилка аналізу тендеру.' : 'Failed to audit tender.'));
            }
          }
        } catch (pollErr) {
          console.error("Job poll error:", pollErr);
        }
      }, 3000);

    } catch (err: any) {
      console.error(err);
      setIsAuditing(false);
      setError(err.message || (isUk ? 'Сталася помилка під час аналізу тендерної документації.' : 'An error occurred during tender audit.'));
    }
  };

  // Download DOCX
  const handleDownloadDocx = async () => {
    if (!tenderReport || isDownloadingDocx) return;
    setIsDownloadingDocx(true);
    try {
      const name = fetchedTenderMeta?.tenderID || (uploadedFiles.length > 0 ? 'prozorro-audit-' + uploadedFiles[0].name.split('.')[0] : 'prozorro-deep-intelligence-report');
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: tenderReport,
          fileName: name
        })
      });

      if (!response.ok) {
        throw new Error(isUk ? 'Помилка формування Word документа.' : 'Failed to generate Word document.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message || (isUk ? 'Не вдалося сформувати DOCX файл.' : 'Failed to create DOCX file.'));
    } finally {
      setIsDownloadingDocx(false);
    }
  };

  const handleCopyReport = () => {
    if (!tenderReport) return;
    navigator.clipboard.writeText(tenderReport);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  if (viewMode === 'generator') {
    return (
      <TenderPackageGeneratorView
        language={language}
        onBack={() => setViewMode('audit')}
        onGoHome={onGoHome}
        useEnterpriseContext={useEnterpriseContext}
        enterpriseName={enterpriseName}
      />
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-16 animate-fadeIn">
      {/* Top Header */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={onGoHome || onBack}
              className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors border border-slate-700 cursor-pointer"
              title={isUk ? "Повернутися назад" : "Go Back"}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center justify-center text-amber-400 shadow-inner">
              <FileSearch className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                  {isUk ? "Prozorro Deep Intelligence" : "Prozorro Deep Intelligence"}
                </h1>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  ЗУ № 922 • ПКМУ № 1178
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                {isUk 
                  ? "Глибокий аудит ТД закупівлі, виявлення дискримінаційних умов для скарг в АМКУ та генерація повного пакета документів учасника" 
                  : "Deep tender specifications audit, AMKU appeal grounds identification, and 100% compliant bidder document generation"}
              </p>
            </div>
          </div>

          <button
            onClick={() => setViewMode('generator')}
            className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/20 cursor-pointer"
          >
            <ShieldAlert className="w-4 h-4" />
            <span>🛡️ Генератор тендерного пакета за ПКМУ № 1178</span>
          </button>
        </div>

        {/* Mode Switcher */}
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-800 flex-wrap">
          <button
            onClick={() => setViewMode('audit')}
            className="px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer bg-amber-500 text-slate-950 font-black shadow-lg shadow-amber-500/20"
          >
            <FileSearch className="w-4 h-4" />
            <span>1. Аудит документації Prozorro (Deep Intelligence)</span>
          </button>
          <button
            onClick={() => setViewMode('generator')}
            className="px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer bg-slate-800 hover:bg-slate-700 text-emerald-400 border border-emerald-500/30"
          >
            <ShieldAlert className="w-4 h-4 text-emerald-400" />
            <span>2. 🛡️ Генератор пакета за Постановою № 1178 (Санкції & 10 Актів)</span>
          </button>
        </div>
      </div>

      {/* Main Input Workspace */}
      {!tenderReport && !isAuditing && (
        <div className="space-y-6">
          {/* 1. Prozorro Live Search & Demo Section */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="text-xs font-bold font-mono text-amber-400 uppercase tracking-wider flex items-center gap-2">
                <Globe className="w-4 h-4 text-amber-400" />
                <span>{isUk ? "1. Прямий імпорт закупівлі з Prozorro (Live Open Data):" : "1. Direct Prozorro Open Data Import:"}</span>
              </label>
              <span className="text-[11px] text-slate-400">
                {isUk ? "Підтримує номери UA-2026-... або посилання prozorro.gov.ua" : "Supports UA-2026-... or Prozorro URLs"}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-2">
              <div className="relative flex-1 w-full">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={prozorroQuery}
                  onChange={(e) => setProzorroQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleFetchProzorro()}
                  placeholder={isUk ? "Введіть номер закупівлі (наприклад: UA-2024-05-18-004512-a) або URL" : "Enter Prozorro tender ID or URL"}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 font-mono transition-colors"
                />
              </div>
              <button
                type="button"
                onClick={handleFetchProzorro}
                disabled={isFetchingProzorro}
                className="w-full sm:w-auto px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 cursor-pointer disabled:opacity-50"
              >
                {isFetchingProzorro ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                <span>{isFetchingProzorro ? (isUk ? "Завантаження..." : "Fetching...") : (isUk ? "⚡ Завантажити з Prozorro" : "⚡ Fetch Prozorro")}</span>
              </button>
            </div>

            {/* Demo Presets */}
            <div className="pt-2 border-t border-slate-800/80">
              <span className="text-[11px] font-mono text-slate-400 block mb-2">
                {isUk ? "Або спробуйте готові демо-приклади публічних закупівель:" : "Or try ready-made demo procurement tenders:"}
              </span>
              <div className="flex flex-wrap gap-2">
                {DEMO_PRESETS.map((preset, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setProzorroQuery(preset.id);
                      setText(preset.text);
                      setError(null);
                    }}
                    className="px-3 py-1.5 bg-slate-800/80 hover:bg-slate-700 text-slate-300 text-xs rounded-lg border border-slate-700 transition-colors flex items-center gap-1.5 cursor-pointer text-left"
                  >
                    <span>{isUk ? preset.titleUk : preset.titleEn}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 2. Text Input & Multi-File Upload */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="text-xs font-bold font-mono text-slate-300 uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-400" />
                <span>{isUk ? "2. Текст тендерної документації, ТЗ або додатки закупівлі:" : "2. Tender Specifications, Criteria, or Annexes:"}</span>
              </label>
              {text && (
                <button
                  type="button"
                  onClick={() => { setText(''); setUploadedFiles([]); setFetchedTenderMeta(null); }}
                  className="text-xs text-rose-400 hover:text-rose-300 transition-colors cursor-pointer"
                >
                  {isUk ? "Очистити поле" : "Clear text"}
                </button>
              )}
            </div>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={9}
              placeholder={isUk 
                ? "Вставте текст ТЗ закупівлі, кваліфікаційні критерії, проект договору або завантажте файли ТД нижче..." 
                : "Paste technical specifications, qualification criteria, contract draft, or upload files below..."}
              className="w-full p-4 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm font-mono text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-400 transition-colors"
            />

            {/* Dropzone */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-slate-950 border border-dashed border-slate-800 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-slate-300">
                  <Upload className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-200">
                    {isUk ? "Завантажити файли тендерної документації (ТД)" : "Upload Tender Documents"}
                  </h4>
                  <p className="text-[11px] text-slate-500">
                    {isUk ? "Підтримуються PDF, DOCX, XLSX, TXT (до 10 файлів одночасно)" : "PDF, DOCX, XLSX, TXT supported"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.doc,.txt,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isExtracting}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-lg border border-slate-700 transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {isExtracting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  <span>{isExtracting ? (isUk ? "Зчитування..." : "Extracting...") : (isUk ? "Обрати файли" : "Select Files")}</span>
                </button>
              </div>
            </div>

            {/* Uploaded files badge list */}
            {uploadedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {uploadedFiles.map((file, idx) => (
                  <span key={idx} className="px-3 py-1 bg-slate-800 text-slate-300 text-xs rounded-md border border-slate-700 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-amber-400" />
                    <span>{file.name}</span>
                    <span className="text-[10px] text-slate-500">({(file.size / 1024).toFixed(1)} KB)</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Error banner */}
          {error && (
            <div className="p-4 bg-rose-950/40 border border-rose-800/80 rounded-xl text-rose-300 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 text-rose-400" />
              <span>{error}</span>
            </div>
          )}

          {/* Action Launch Button */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Scale className="w-4 h-4 text-amber-400" />
              <span>
                {isUk 
                  ? "Аудит проводиться на відповідність ст. 5, 16, 17, 22 ЗУ «Про публічні закупівлі» та ПКМУ № 1178" 
                  : "Audit complies with Public Procurement Law of Ukraine & Resolution #1178"}
              </span>
            </div>

            <button
              type="button"
              onClick={handleRunAudit}
              disabled={isAuditing || !text.trim()}
              className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-sm uppercase tracking-wider rounded-xl transition-all shadow-xl shadow-amber-500/25 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-5 h-5 text-slate-950" />
              <span>{isUk ? "🚀 Запустити Повний Аудит Закупівлі" : "🚀 Launch Deep Tender Audit"}</span>
            </button>
          </div>
        </div>
      )}

      {/* Loading Progress State */}
      {isAuditing && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center space-y-6 animate-pulse">
          <div className="w-16 h-16 bg-amber-500/20 border border-amber-500/40 rounded-2xl mx-auto flex items-center justify-center text-amber-400 animate-spin">
            <RefreshCw className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-bold text-white">
              {isUk ? "ШІ-мультиагентний аудит тендерної документації..." : "AI Multi-Agent Tender Audit in Progress..."}
            </h3>
            <p className="text-sm font-mono text-amber-400 max-w-xl mx-auto">
              {isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}
            </p>
          </div>

          <div className="w-full bg-slate-950 h-2 rounded-full overflow-hidden border border-slate-800 max-w-md mx-auto">
            <div 
              className="bg-amber-500 h-full transition-all duration-700" 
              style={{ width: `${((loadingStepIndex + 1) / LOADING_STEPS_UK.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Results View */}
      {tenderReport && !isAuditing && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-6 shadow-2xl animate-fadeIn">
          {/* Actions Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-emerald-500 rounded-full animate-ping" />
              <h3 className="text-sm font-black text-white uppercase tracking-wider">
                {isUk ? "Підсумковий Експертний Звіт & Пакет Документів Учасника:" : "Procurement Audit Report & Bidder Documents Package:"}
              </h3>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Telegram Export Button */}
              <TelegramExportButton
                title={fetchedTenderMeta?.title || "Аудит закупівлі Prozorro"}
                content={tenderReport}
                agentName="Prozorro Deep Intelligence"
                agentId="tender-analyst"
                isUk={isUk}
                variant="primary"
              />

              {/* Formular Excel Export Button */}
              <ExcelExportButton
                title={fetchedTenderMeta?.title || "Тендерний Розрахунок"}
                content={tenderReport}
                fileName={fetchedTenderMeta?.tenderID || "prozorro_calculation"}
                isUk={isUk}
                variant="emerald"
              />

              {/* Word DOCX Download */}
              <button
                type="button"
                onClick={handleDownloadDocx}
                disabled={isDownloadingDocx}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase rounded-lg transition-colors flex items-center gap-1.5 shadow-md shadow-amber-500/20 cursor-pointer disabled:opacity-50"
              >
                {isDownloadingDocx ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                <span>{isUk ? "Word (.docx)" : "Word (.docx)"}</span>
              </button>

              {/* Copy Report */}
              <button
                type="button"
                onClick={handleCopyReport}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 border border-slate-700 cursor-pointer"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? "Скопійовано!" : "Copied!") : (isUk ? "Скопіювати" : "Copy")}</span>
              </button>

              {/* Print / PDF */}
              <button
                type="button"
                onClick={() => window.print()}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 border border-slate-700 cursor-pointer"
              >
                <Printer className="w-3.5 h-3.5 text-amber-400" />
                <span>{isUk ? "Друк / PDF" : "Print / PDF"}</span>
              </button>

              {/* New Audit */}
              <button
                type="button"
                onClick={() => { setTenderReport(null); setError(null); }}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 border border-slate-700 cursor-pointer"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>{isUk ? "Новий аналіз" : "New Audit"}</span>
              </button>
            </div>
          </div>

          {/* Formatted Content Viewer */}
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 text-xs sm:text-sm font-mono text-slate-200 leading-relaxed whitespace-pre-wrap max-h-[750px] overflow-y-auto shadow-inner">
            {tenderReport}
          </div>
        </div>
      )}
    </div>
  );
}
